import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { CITY_FALLBACK_TYPES, findFirstComponent } from './address';
import { isNonUsResult } from './selectBestResult';

// Re-geocode and diagnose fields

const COMPONENT_LABELS = {
    street_number: 'Street number',
    route: 'Street name',
    locality: 'City',
    administrative_area_level_1: 'State',
    postal_code: 'Zip',
};

const COMPONENT_TO_FIELD = {
    street_number: 'street',
    route: 'street',
    locality: 'city',
    administrative_area_level_1: 'state',
    postal_code: 'zip',
};

export default async function diagnoseAddress(address) {
    if (!address || typeof address !== 'string' || address.trim().length === 0) {
        return { ok: false, kind: 'no-results', message: 'No address to verify.' };
    }

    let results;
    try {
        results = await geocodeByAddress(address);
    } catch (err) {
        return {
            ok: false,
            kind: 'error',
            message: err?.message || 'Google geocoder returned an error.',
        };
    }

    if (!results || results.length === 0) {
        return {
            ok: false,
            kind: 'no-results',
            message: 'Google found no matching address.',
        };
    }

    // Exact-match diagnostics; persist results[0]
    const exact = results.find((r) => !r.partial_match);
    const best = exact || results[0];
    const components = best.address_components || [];
    const formatted = best.formatted_address;

    if (isNonUsResult(best)) {
        const country = findFirstComponent(components, ['country']);
        return {
            ok: false,
            kind: 'non-us',
            message: `Address resolved outside the US (${country?.long_name || 'non-US'}).`,
            formatted,
        };
    }

    const issues = [];
    const streetNumber = findFirstComponent(components, ['street_number']);
    const route = findFirstComponent(components, ['route']);
    const locality = findFirstComponent(components, CITY_FALLBACK_TYPES);
    const adminArea = findFirstComponent(components, ['administrative_area_level_1']);
    const postalCode = findFirstComponent(components, ['postal_code']);

    if (!streetNumber) {
        issues.push({ field: COMPONENT_TO_FIELD.street_number, label: COMPONENT_LABELS.street_number });
    }
    if (!route) {
        issues.push({ field: COMPONENT_TO_FIELD.route, label: COMPONENT_LABELS.route });
    }
    if (!locality) {
        issues.push({ field: COMPONENT_TO_FIELD.locality, label: COMPONENT_LABELS.locality });
    }
    if (!adminArea) {
        issues.push({
            field: COMPONENT_TO_FIELD.administrative_area_level_1,
            label: COMPONENT_LABELS.administrative_area_level_1,
        });
    }
    if (!postalCode) {
        issues.push({ field: COMPONENT_TO_FIELD.postal_code, label: COMPONENT_LABELS.postal_code });
    }

    let lat = null;
    let lng = null;
    try {
        const coords = await getLatLng(best);
        lat = coords.lat;
        lng = coords.lng;
    } catch {
        // getLatLng throws when the result has no geometry — surface it as
        // missing coords without crashing the diagnostic.
    }

    if (issues.length === 0 && best.partial_match) {
        // Google flagged it as partial but every named component is present;
        // still worth telling the operator the match isn't exact.
        return {
            ok: false,
            kind: 'partial',
            message: `Google returned a partial match (${formatted}). Re-enter from the dropdown to confirm.`,
            formatted,
            issues: [],
            suggested: { lat, lng },
        };
    }

    if (issues.length > 0) {
        const labels = issues.map((i) => i.label).join(', ');
        return {
            ok: false,
            kind: 'partial',
            message: `${labels} couldn't be resolved. Google matched "${formatted}".`,
            formatted,
            issues,
            suggested: { lat, lng },
        };
    }

    return { ok: true, lat, lng, formatted };
}
